/* eslint-disable eqeqeq */
import { PlusOutlined, EditOutlined, InboxOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  message,
  Tabs,
  Upload,
  Spin,
  InfoCircleOutlined,
} from "antd";
import axios from "axios";
import { getCookie } from "cookies-next";
import React, { useEffect, useRef, useState } from "react";
import { FaRegQuestionCircle } from "react-icons/fa";
import { LuLayoutDashboard } from "react-icons/lu";
import Skeleton from "react-loading-skeleton";
import CheckMark from '../../../../assets/check.svg';
import { makeApiRequest } from '../../../../shared/api';
import styles from "./style.module.scss";
import { v4 as uuidv4 } from "uuid";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Dragger } = Upload;

const KnowledgeVerification = ({ setCurrent, setKbLibrary }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [, setIsEditing] = useState({});
  const [kbData, setKbData] = useState(null);
  const [content, setContent] = useState({});
  const [originalContent, setOriginalContent] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [approvedItems, setApprovedItems] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);
  const [isApproving, setIsApproving] = useState({});
  const [form] = Form.useForm();
  const [questions, setQuestions] = useState({});
  const [originalQuestions, setOriginalQuestions] = useState({});
  const [openAddQuestionModal, setOpenAddQuestionModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [isDeleting, setIsDeleting] = useState({});
  const [allQuestionsEffectivelyApproved, setAllQuestionsEffectivelyApproved] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [newlyAddedQuestions, setNewlyAddedQuestions] = useState([]);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [questionForGeneration, setQuestionForGeneration] = useState(null);
  const [isAdditionalInfoModalVisible, setIsAdditionalInfoModalVisible] = useState(false);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [additionalInfoContent, setAdditionalInfoContent] = useState({});
  const [additionalInfoForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("single");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [isProcessingModalVisible, setIsProcessingModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState(""); 

  const KB_ID = getCookie("fb_kb_id");
  
  const cardItems = [
    {
      title: "Categories",
      value: categoriesLoaded ? categories.length : 0,
      icon: <LuLayoutDashboard size={20} />,
    },
    {
      title: "Questions",
      value: kbData?.kb_answers?.length ?? 0,
      icon: <FaRegQuestionCircle size={20} />,
    },
  ];

  const onChange = (value) => {
    setSelectedCategory(value === undefined ? null : value);
  };

  const handleCollapseChange = (keys) => {
    setActiveKeys(keys);
  };

  const handleApproveAll = async() => {
    if (!selectedCategory) {
      message.error("Please select a category to approve.");
      return;
    }

    try {
      setIsLoading(true);

      let kb_answers_to_filter = kbData?.kb_answers || [];

      let kb_answers_to_approve = kb_answers_to_filter;
      if (selectedCategory !== "All Categories") {
        kb_answers_to_approve = kb_answers_to_filter.filter(
          (item) => item.question_category === selectedCategory
        );
      }

      if (!kb_answers_to_approve || kb_answers_to_approve.length === 0) {
        const msg = selectedCategory === "All Categories"
          ? "No questions found in the knowledge base to approve."
          : `No questions found in category: ${selectedCategory} to approve.`;
        message.warning(msg);
        setIsLoading(false);
        return;
      }

      const items_needing_approval = kb_answers_to_approve.filter(item => {
        const hasBeenEdited = (content[item.question_index] !== originalContent[item.question_index]) ||
                                (questions[item.question_index] !== originalQuestions[item.question_index]);
        return !approvedItems[item.question_index] || hasBeenEdited;
      });

      if(items_needing_approval.length === 0) {
        message.info(`All questions in ${selectedCategory === "All Categories" ? "the knowledge base" : `category '${selectedCategory}'`} are already approved and unchanged.`);
        setIsLoading(false);
        return;
      }

      const payload = items_needing_approval.map((item) => ({
        question_index: item.question_index,
        question_text: questions[item.question_index] ?? item.question_text,
        answer_text: content[item.question_index] ?? item.answer_text,
        question_category: item.question_category,
        status: "Approved",
        layout_id: "HTML_LAYOUT_01",
        additional_info: additionalInfoContent[item.question_index] || null
      }));

      const response = await makeApiRequest(
        "PUT",
        `kb/${KB_ID}/questions`,
        payload
      );

      if (response) {
        const successMsg = selectedCategory === "All Categories"
          ? `Successfully approved ${payload.length} question(s).`
          : `Successfully approved ${payload.length} question(s) in category: ${selectedCategory}`;
        message.success(successMsg);

        const updatedApprovedItems = { ...approvedItems };
        const updatedOriginalContent = { ...originalContent };
        const updatedOriginalQuestions = { ...originalQuestions };

        payload.forEach((approvedItem) => {
          updatedApprovedItems[approvedItem.question_index] = true;
          updatedOriginalContent[approvedItem.question_index] = approvedItem.answer_text;
          updatedOriginalQuestions[approvedItem.question_index] = approvedItem.question_text;
        });

        setApprovedItems(updatedApprovedItems);
        setOriginalContent(updatedOriginalContent);
        setOriginalQuestions(updatedOriginalQuestions);

        checkAllEffectivelyApproved();

      } else {
        const errorMsg = selectedCategory === "All Categories"
          ? "Failed to approve questions."
          : `Failed to approve questions in category: ${selectedCategory}.`;
        message.error(errorMsg);
      }
    } catch (error) {
      console.error("Approve All Error:", error);
      message.error("An error occurred while approving the category.");
    } finally {
      setIsLoading(false);
    }
  };

  const options = [...categories, "All Categories"].map((category) => ({
    label: category,
    value: category,
  }));

  const modelOptions = categories?.map((category) => ({
    label: category,
    value: category,
  }));

  const handleTextChange = (question_index, newText) => {
    setContent((prev) => ({
      ...prev,
      [question_index]: newText,
    }));

    setApprovedItems((prev) => {
      if (prev[question_index] && newText !== originalContent[question_index]) {
        const { [question_index]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
    checkAllEffectivelyApproved();
  };

  const handleAdditionalInfo = (item) => {
    setSelectedQuestionIndex(item.question_index);
    additionalInfoForm.setFieldsValue({
      additional_info: additionalInfoContent[item.question_index] || "",
    });
    setIsAdditionalInfoModalVisible(true);
  };

  const handleAdditionalInfoSave = async () => {
    try {
      const values = await additionalInfoForm.validateFields();
      if (!values.additional_info) {
        message.warning("Please enter additional information");
        return;
      }

      setAdditionalInfoContent((prev) => ({
        ...prev,
        [selectedQuestionIndex]: values.additional_info,
      }));
      message.success("Additional information saved successfully");
      setIsAdditionalInfoModalVisible(false);
    } catch (error) {
      console.error("Error saving additional info:", error);
      message.error("Failed to save additional information");
    }
  };

  const handleQuestionChange = (question_index, newQuestion) => {
    setQuestions((prev) => ({
      ...prev,
      [question_index]: newQuestion,
    }));

    setApprovedItems((prev) => {
      if (prev[question_index] && newQuestion !== originalQuestions[question_index]) {
        const { [question_index]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
    checkAllEffectivelyApproved();
  };

  const handleApprove = async(item) => {
    try {
      setIsApproving((prev) => ({ ...prev, [item.question_index]: true }));
      const answer = content[item.question_index] ?? item.answer_text;
      const question = questions[item.question_index] ?? item.question_text;
      const additionalInfo = additionalInfoContent[item.question_index] || null;

      const response = await makeApiRequest("PUT", `/kb/${item.kb_id}/questions/${item.question_index}`, {
        question_text: question,
        answer_text: answer,
        question_category: item.question_category,
        status: "Approved",
        layout_id: "HTML_LAYOUT_01", // Add default layout ID
        additional_info: additionalInfo
      });

      if (response) {
        message.success("Successfully approved question");
        setApprovedItems((prev) => ({ ...prev, [item.question_index]: true }));

        setOriginalContent(prev => ({...prev, [item.question_index]: answer}));
        setOriginalQuestions(prev => ({...prev, [item.question_index]: question}));

        checkAllEffectivelyApproved();
      } else {
        message.error("Failed to approve question");
      }
    } catch (error) {
      console.error("Approve Error:", error);
      message.error("An error occurred while approving the question.");
    } finally {
      setIsApproving((prev) => ({ ...prev, [item.question_index]: false }));
    }
  };

  const handleDelete = async(item) => {
    try {
      setIsDeleting((prev) => ({ ...prev, [item.question_index]: true }));
      const response = await makeApiRequest("DELETE", `/kb/${item.kb_id}/questions/${item.question_index}`);

      if (response) {
        message.success("Successfully deleted question");
        GetKb();
        setKbData((prevKbData) => {
          if (!prevKbData || !prevKbData.kb_answers) return prevKbData;
          const updatedKbAnswers = prevKbData.kb_answers.filter(
            (answer) => answer.question_index !== item.question_index
          );
          return { ...prevKbData, kb_answers: updatedKbAnswers };
        });

        setContent((prev) => { const { [item.question_index]: r, ...rest } = prev; return rest; });
        setQuestions((prev) => { const { [item.question_index]: r, ...rest } = prev; return rest; });
        setApprovedItems((prev) => { const { [item.question_index]: r, ...rest } = prev; return rest; });
        setOriginalContent((prev) => { const { [item.question_index]: r, ...rest } = prev; return rest; });
        setOriginalQuestions((prev) => { const { [item.question_index]: r, ...rest } = prev; return rest; });
        setIsEditing((prev) => { const { [item.question_index]: r, ...rest } = prev; return rest; });

        checkAllEffectivelyApproved();
      } else {
        message.error("Failed to delete question.");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      message.error("An error occurred while deleting the question.");
    } finally {
      setIsDeleting((prev) => ({ ...prev, [item.question_index]: false }));
    }
  };

  const checkAllEffectivelyApproved = () => {
    if (!kbData?.kb_answers || kbData.kb_answers.length === 0) {
      setAllQuestionsEffectivelyApproved(true);
      return;
    }

    const allApproved = kbData.kb_answers.every(item => {
      const hasBeenEdited = (content[item.question_index] !== originalContent[item.question_index]) ||
                              (questions[item.question_index] !== originalQuestions[item.question_index]);
      return approvedItems[item.question_index] && !hasBeenEdited;
    });
    setAllQuestionsEffectivelyApproved(allApproved);
  };

  useEffect(() => {
    if (KB_ID) {
      GetKb();
    } else {
      message.error("Knowledge Base ID not found. Cannot load data.");
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [KB_ID]);

  useEffect(() => {
    checkAllEffectivelyApproved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedItems, kbData, content, questions, originalContent, originalQuestions]);

  const GetKb = async() => {
    if (!KB_ID) return;

    try {
      setIsLoading(true);
      setCategoriesLoaded(false);
      const response = await makeApiRequest("GET", `kb/${KB_ID}/questions`);

      if (Array.isArray(response)) {
        setKbData({ kb_answers: response });

        const uniqueCategories = [
          ...new Set(response.map((item) => item.question_category)
            .filter((category) => category && category.trim() !== "")
          ),
        ];
        const kbName = response[0]?.kb_name;
        if (setKbLibrary && kbName) {
          setKbLibrary([kbName]);
        }

        setCategories(uniqueCategories);
        setCategoriesLoaded(true);

        const initialContent = {};
        const initialQuestions = {};
        const initialApprovedItems = {};
        const initialOriginalContent = {};
        const initialOriginalQuestions = {};

        response.forEach((item) => {
          initialContent[item.question_index] = item.answer_text;
          initialQuestions[item.question_index] = item.question_text;
          initialApprovedItems[item.question_index] = item.status === 'Approved';
          initialOriginalContent[item.question_index] = item.answer_text;
          initialOriginalQuestions[item.question_index] = item.question_text;
        });

        setContent(initialContent);
        setQuestions(initialQuestions);
        setApprovedItems(initialApprovedItems);
        setOriginalContent(initialOriginalContent);
        setOriginalQuestions(initialOriginalQuestions);
        setNewlyAddedQuestions([]);

      } else {
        message.error("Failed to load knowledge base data. Invalid data format received.");
        setKbData({ kb_answers: [] });
        setCategories([]);
        setCategoriesLoaded(true);
        setNewlyAddedQuestions([]);
      }
    } catch (error) {
      console.error("GetKb Error:", error);
      message.error("Failed to load knowledge base data.");
      setKbData({ kb_answers: [] });
      setCategories([]);
      setCategoriesLoaded(true);
      setNewlyAddedQuestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const skeletonItems = Array.from({ length: 5 }).map((_, index) => ({
    key: `skeleton-${index}`,
    label: <Skeleton width="80%" />,
    children: ( <div> <Skeleton count={3} /> </div> ),
    extra: <Skeleton circle width={20} height={20} />,
  }));

  const filteredKbAnswers = (selectedCategory && selectedCategory !== "All Categories")
    ? kbData?.kb_answers?.filter(
      (item) => item.question_category === selectedCategory
    )
    : (kbData?.kb_answers || []);

  const items =
    (filteredKbAnswers || []).map((item) => {
      const hasBeenEdited = (content[item.question_index] !== originalContent[item.question_index]) ||
                              (questions[item.question_index] !== originalQuestions[item.question_index]);
      const isEffectivelyApproved = approvedItems[item.question_index] && !hasBeenEdited;

      return {
        key: item.question_index,
        label: (
          <Text
            editable={{
              onChange: (value) =>
                handleQuestionChange(item.question_index, value),
              triggerType: "icon",
              icon: <EditOutlined />,
              tooltip: false,
            }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "calc(100% - 30px)",
            }}
          >
            {`${item.question_index + 1}. ${(
              questions[item.question_index] || item.question_text
            ).replace(/^\d+\.\s*/, "")}`}
          </Text>
        ),
        children: (
          <div>
              <Text
                editable={{
                  onChange: (value) =>
                    handleTextChange(item.question_index, value),
                  triggerType: "icon", // edit on icon only, cleaner UX
                  icon: <EditOutlined />,
                  tooltip: "Click to edit", // built-in tooltip support
                }}
                style={{ display: "block", marginBottom: "10px" }}
              >
                {content[item.question_index] ??
                  item.answer_text ??
                  "No answer provided.."}
              </Text>
            <Flex justify="flex-end" gap={16}>
              <Button
                type="primary"
                size="large"
                onClick={() => handleAdditionalInfo(item)}
              >
                Additional Info
              </Button>
              <Button
                type="primary"
                danger
                size="large"
                loading={isDeleting[item.question_index]}
                onClick={() => handleDelete(item)}
                disabled={isDeleting[item.question_index]}
              >
                Delete
              </Button>
              <Button
                type="primary"
                size="large"
                style={{
                  backgroundColor: isEffectivelyApproved ? "gray" : "#2fcc71",
                  color: "white",
                }}
                onClick={() => handleApprove(item)}
                disabled={
                  isEffectivelyApproved || isApproving[item.question_index]
                }
                loading={isApproving[item.question_index]}
              >
                {isEffectivelyApproved ? "Approved" : "Approve"}
              </Button>
            </Flex>
          </div>
        ),
        extra: isEffectivelyApproved ? (
          <img
            src={CheckMark}
            alt="Approved"
            style={{
              width: "20px",
              height: "20px",
              filter:
                "invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)",
            }}
          />
        ) : null,
      };
    });

  const newlyAddedItems = newlyAddedQuestions.map(item => {
    const hasBeenEdited = (content[item.question_index] !== originalContent[item.question_index]) ||
                           (questions[item.question_index] !== originalQuestions[item.question_index]);
    const isEffectivelyApproved = approvedItems[item.question_index] && !hasBeenEdited;

    return {
      key: item.question_index,
      label: (
        <Text
          editable={{
            onChange: (value) =>
              handleQuestionChange(item.question_index, value),
          }}
        >
          {`${item.question_index + 1}. ${(
            questions[item.question_index] || item.question_text
          ).replace(/^\d+\.\s*/, "")}`}
        </Text>
      ),
      children: (
        <div>
          <Text
            editable={{
              onChange: (value) => handleTextChange(item.question_index, value),
              triggerType: "icon",
              icon: <EditOutlined />,
              tooltip: "Click to edit answer",
            }}
            style={{ display: "block", marginBottom: "10px" }}
          >
            {content[item.question_index] ?? item.answer_text ?? ""}
          </Text>
          <Flex justify="flex-end" gap={16}>
            <Button
              type="primary"
              size="large"
              onClick={() => handleAdditionalInfo(item)}
            >
              Additional Info
            </Button>
            <Button
              type="primary"
              danger
              size="large"
              loading={isDeleting[item.question_index]}
              onClick={() => handleDelete(item)}
              disabled={isDeleting[item.question_index]}
            >
              Delete
            </Button>
            <Button
              type="primary"
              size="large"
              style={{
                backgroundColor: isEffectivelyApproved ? "gray" : "#2fcc71",
                color: "white",
              }}
              onClick={() => handleApprove(item)}
              disabled={
                isEffectivelyApproved || isApproving[item.question_index]
              }
              loading={isApproving[item.question_index]}
            >
              {isEffectivelyApproved ? "Approved" : "Approve"}
            </Button>
          </Flex>
        </div>
      ),
      extra: isEffectivelyApproved ? (
        <img
          src={CheckMark}
          alt="Approved"
          style={{
            width: "20px",
            height: "20px",
            filter:
              "invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)",
          }}
        />
      ) : null,
    };
  });

  const handleAddQuestion = () => {
    form.resetFields();
    setOpenAddQuestionModal(true);
  };

  const handleAddQuestionModalOk = async() => {
    setConfirmModalVisible(false);
    try {
      const values = await form.validateFields();
      console.log("Form values:", values);
      setIsLoading(true);
  
      const existingMaxIndex = kbData?.kb_answers?.length > 0
      ? Math.max(...kbData.kb_answers.map(item => item.question_index))
      : 0;
    
      const newlyAddedMaxIndex = newlyAddedQuestions.length > 0
      ? Math.max(...newlyAddedQuestions.map(item => item.question_index))
      : 0;
      const nextQuestionIndex = Math.max(existingMaxIndex, newlyAddedMaxIndex) + 1;
  
      const payload = {
        question_index: nextQuestionIndex,
        question_text: values.question,
        answer_text: values.answer,
        question_category: values.category,
        status: "Approved",
      };
  
      const response = await makeApiRequest(
        "POST",
        `kb/${KB_ID}/questions`,
        payload
      );
  
      if (response) {
        message.success("Successfully added new question.");
  
        const newItem = {
          ...payload,
          kb_id: KB_ID,
        };
  
        setNewlyAddedQuestions((prev) => [newItem, ...prev]);
        setApprovedItems((prev) => ({ ...prev, [payload.question_index]: true }));
        // const updatedKbAnswers = kbData?.kb_answers ? [newItem, ...kbData.kb_answers] : [newItem];
        // setKbData((prev) => ({
        // ...prev,
        // kb_answers: updatedKbAnswers,
        // }));
  
        setContent((prevContent) => ({
          ...prevContent,
          [nextQuestionIndex]: payload.answer_text,
        }));
  
        setQuestions((prevQuestions) => ({
          ...prevQuestions,
          [nextQuestionIndex]: payload.question_text,
        }));
  
        setOriginalContent((prevOriginalContent) => ({
          ...prevOriginalContent,
          [nextQuestionIndex]: payload.answer_text,
        }));
  
        setOriginalQuestions((prevOriginalQuestions) => ({
          ...prevOriginalQuestions,
          [nextQuestionIndex]: payload.question_text,
        }));
  
        setOpenAddQuestionModal(false);
  
      } else {
        message.error("Failed to add new question");
      }
    } catch (errorInfo) {
      console.log(errorInfo);
      
      if (errorInfo.errorFields) {
        console.log('Validation Failed:', errorInfo);
      } else {
        console.error("Error adding new question:", errorInfo);
        message.error("An error occurred while adding the question.");
      }
    } finally {
      setIsLoading(false);
      setQuestionForGeneration(null);
  
    }
  };

  const handleAddQuestionModalCancel = () => {
    setOpenAddQuestionModal(false);
    form.resetFields();
  };

  const inputRef = useRef(null);

  const onNameChange = (event) => {
    setNewCategory(event.target.value);
  };

  const addItem = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmedCategory = newCategory.trim();

    if (trimmedCategory && !categories.includes(trimmedCategory)) {
      const updatedCategories = [...categories, trimmedCategory].sort();
      setCategories(updatedCategories);

      form.setFieldsValue({ category: trimmedCategory });

      setNewCategory("");

      setTimeout(() => { inputRef.current?.focus(); }, 0);

      message.success("Successfully added new category");
    } else if (categories.includes(trimmedCategory)) {
      message.warning("Category already exists");
    } else {
      message.warning("Please enter a category name");
    }
  };

  const fileTypeMap = {
    "application/pdf": "pdf",
    "text/plain": "txt",
    "text/html": "html",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  const getUploadUrl = async (file) => {
    const newUuid = uuidv4();
    const params = {
      file_id: newUuid.slice(0, 8),
      file_type: fileTypeMap[file.type] || file.type.split("/")[1],
    };
    return await makeApiRequest("POST", "kb/requestUploadUrl", params);
  };

  const handleContinueUpload = async () => {
    setIsProcessingModalVisible(true);
    const initialQuestionCount = kbData?.kb_answers?.length || 0;
    if (fileList.length === 0) {
      message.error('Please select a file first');
      return;
    }
    const file = fileList[0];
    if (!file || !(file instanceof File || file instanceof Blob)) {
      message.error("Invalid file object. Please select a file again.");
      setFileList([]);
      setSelectedFileName("");
      return;
    }
    setUploadLoading(true);
    const uploadData = await getUploadUrl(file);
    if (!uploadData) {
      message.error('Failed to get upload URL');
      setUploadLoading(false);
      return;
    }
    const formData = new FormData();
    if (uploadData.fields) {
      for (const key in uploadData.fields) {
        formData.append(key, uploadData.fields[key]);
      }
    }
    try {
      formData.append('file', file, file.name || 'uploaded-file');
    } catch (formDataError) {
      console.error('FormData append error:', formDataError);
      message.error('Failed to prepare file for upload. Please try again.');
      setUploadLoading(false);
      return;
    }
    await axios.post(uploadData.url, formData, {
      headers: {},
      transformRequest: [function (data) { return data; }],
    });
    const fileUrl = `${uploadData.url}${uploadData.fields.key}`;
    await makeApiRequest("PUT", `kb/${KB_ID}`, { kb_file_urls: [fileUrl] });
    message.success('File uploaded successfully');
    try {
      const checkKBStatus = async (retries = 0) => {
        if (retries > 30) {
          message.error("Processing timed out. Please try again later.");
          setIsProcessingModalVisible(false);
          setOpenAddQuestionModal(false);
          return;
        }
        try {
          const response = await makeApiRequest("GET", `kb/${KB_ID}`);
          const newQuestionCount = response?.kb_answers?.length || 0;
          if (response && response.progress === 100 && response.kb_status === 'COMPLETED') {
            if (newQuestionCount > initialQuestionCount) {
              message.success('Questions added successfully!');
            }
            GetKb();
            setIsProcessingModalVisible(false);
            setOpenAddQuestionModal(false);
          } else {
            setTimeout(() => checkKBStatus(retries + 1), 2000);
          }
        } catch (err) {
          message.error("An error occurred while checking processing status.");
          setIsProcessingModalVisible(false);
          setOpenAddQuestionModal(false);
        }
      };
      await checkKBStatus();
    } catch (error) {
      message.error('Failed to upload file: ' + (error.response?.data?.message || error.message));
      setIsProcessingModalVisible(false);
      setOpenAddQuestionModal(false);
    } finally {
      setUploadLoading(false);
      setFileList([]);
      setSelectedFileName('');
    }
  };

  const draggerProps = {
    accept: '.pdf,.doc,.docx,.txt,.html',
    fileList: fileList,
    beforeUpload: (file) => {
      const maxSize = 1 * 1024 * 1024;
      const allowedTypes = {
        'application/pdf': 'pdf',
        'text/html': 'html',
        'text/plain': 'txt',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
      };
      if (!Object.keys(allowedTypes).includes(file.type)) {
        message.error("Invalid file type. Only PDF, HTML, TXT, and Word files are allowed.");
        return false;
      }
      if (file.size > maxSize) {
        message.error("File size must be 1MB or less. Please try again!");
        return false;
      }
      if (!(file instanceof File)) {
        message.error("Invalid file object. Please try again.");
        return false;
      }
      setSelectedFileName(file.name);
      setFileList([file]);
      return false;
    },
    showUploadList: false,
    onChange(info) {
      if (info.fileList.length > 0) {
        const file = info.fileList[info.fileList.length - 1];
        if (file && file.originFileObj) {
          setFileList([file.originFileObj]);
        } else if (file && (file instanceof File || file instanceof Blob)) {
          setFileList([file]);
        }
      } else {
        setFileList([]);
      }
    }
  };

  return (
    <div className={styles.container}>
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={14}>
          <Title level={3} style={{ fontWeight: "normal" }}>
            <EditOutlined
              style={{ fontSize: "16px", color: "#08c", marginRight: "10px" }}
            />
            {isLoading && !kbData ? (
              <Skeleton width={200} />
            ) : (
              "Knowledge Verification & Editing"
            )}
          </Title>
          {isLoading && !kbData ? (
            <Skeleton count={2} />
          ) : (
            <Text className={styles.description}>
              This process involves thoroughly reviewing and confirming the
              accuracy, relevance, and completeness of information related to
              your products, services, or processes. It ensures that all data is
              up-to-date, factual, and aligned with your objectives. The editing
              phase focuses on refining the content for clarity, consistency,
              and proper structure, ensuring it is presented in a professional,
              easy-to-understand manner. This step is crucial for maintaining
              the integrity of your materials, boosting credibility, and
              facilitating effective communication.
            </Text>
          )}

          <Title level={4} style={{ fontWeight: "normal", marginTop: "4rem" }}>
            {isLoading && !kbData ? (
              <Skeleton width={250} />
            ) : (
              "Key Findings on Product / Service Performance"
            )}
          </Title>

          <Flex wrap="wrap" gap="large" className={styles.cardContainer}>
            {isLoading && !categoriesLoaded
              ? Array.from({ length: 2 }).map((_, index) => (
                  <div
                    className={styles.card}
                    key={index}
                    style={{
                      padding: "20px",
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                      minWidth: "150px",
                    }}
                  >
                    <Skeleton
                      height={20}
                      width={100}
                      style={{ marginBottom: "10px" }}
                    />
                    <Skeleton height={30} width={50} />
                  </div>
                ))
              : cardItems.map((item, index) => (
                  <div className={styles.card} key={index}>
                    <Space>
                      {item.icon}
                      <Text className={styles.cardTitle}>{item.title}</Text>
                    </Space>
                    <Title level={2} className={styles.cardValue}>
                      {item.value}
                    </Title>
                  </div>
                ))}
          </Flex>

          <Title
            level={4}
            style={{
              fontWeight: "normal",
              marginTop: "4rem",
              marginBottom: "1rem",
            }}
          >
            {isLoading && !kbData ? (
              <Skeleton width={400} />
            ) : (
              <span>
                Please verify the contents to ensure accuracy & completeness.
                <br />
                Approve items individually or by the selected category.
              </span>
            )}
          </Title>

          <Space wrap style={{ marginBottom: "2rem" }}>
            {isLoading && !categoriesLoaded ? (
              <Skeleton width={200} height={40} />
            ) : (
              <Select
                showSearch
                placeholder="Select a category"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={selectedCategory}
                onChange={onChange}
                options={options}
                allowClear
                size="large"
                style={{ width: "20rem" }}
                disabled={isLoading}
              />
            )}
            <Button
              type="primary"
              size="large"
              style={{ width: "10rem" }}
              onClick={handleApproveAll}
              disabled={isLoading || !selectedCategory}
            >
              {isLoading ? <Skeleton width={80} /> : "Approve all"}
            </Button>
            <Button
              type="primary"
              size="large"
              style={{ width: "10rem" }}
              onClick={handleAddQuestion}
              disabled={isLoading}
            >
              {isLoading ? <Skeleton width={80} /> : "Add new question"}
            </Button>
          </Space>

          {newlyAddedQuestions.length > 0 && (
            <div className={styles.approveCard}>
              <Title level={5}>Newly Added Questions</Title>
              <Collapse
                activeKey={activeKeys}
                onChange={handleCollapseChange}
                expandIconPosition="end"
                size="large"
                items={newlyAddedItems}
              />
            </div>
          )}

          <div className={styles.approveCard}>
            <Form form={form} component={false}>
              <Collapse
                activeKey={activeKeys}
                onChange={handleCollapseChange}
                expandIconPosition="end"
                size="large"
                items={isLoading && !kbData ? skeletonItems : items}
              />
            </Form>
          </div>
        </Col>
      </Row>

      {!isLoading && kbData && (
        <div
          className={styles.hoverFooterWrapper}
          style={{
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          <div>
            <Title
              level={4}
              style={{ margin: 0, fontWeight: "normal", color: "gray" }}
            >
              Knowledge Base Summary
            </Title>
          </div>
          <span className={styles.sectionShadow} />
          <div className={styles.section}>
            <Title level={3} style={{ margin: 0, fontWeight: "normal" }}>
              {!categoriesLoaded ? (
                <Skeleton width={30} inline />
              ) : (
                categories?.length || 0
              )}
            </Title>
            <LuLayoutDashboard
              size={22}
              color="gray"
              style={{ marginLeft: "8px", marginRight: "4px" }}
            />
            <Text style={{ color: "gray" }}>Categories</Text>
          </div>
          <div className={styles.section}>
            <Title level={3} style={{ margin: 0, fontWeight: "normal" }}>
              {kbData?.kb_answers?.length ?? 0}
            </Title>
            <FaRegQuestionCircle
              size={22}
              color="gray"
              style={{ marginLeft: "8px", marginRight: "4px" }}
            />
            <Text style={{ color: "gray" }}>Questions</Text>
          </div>
          <div>
            <Button
              type="primary"
              disabled={isLoading || !allQuestionsEffectivelyApproved}
              onClick={() => {
                if (setCurrent) setCurrent(3);
              }}
              size="large"
              style={{ width: "100%" }}
            >
              Save & Continue
            </Button>
            {!allQuestionsEffectivelyApproved && (
              <Text
                type="warning"
                style={{
                  display: "block",
                  textAlign: "center",
                  marginTop: "5px",
                }}
              >
                Approve all changes before continuing.
              </Text>
            )}
          </div>
        </div>
      )}

      <Modal
        open={openAddQuestionModal}
        onOk={handleAddQuestionModalOk}
        onCancel={handleAddQuestionModalCancel}
        width={600}
        centered
        maskClosable={true}
        title="Add New Question"
        destroyOnClose
        footer={
          activeTab === "single" ? (
            <>
              <Button key="back" onClick={handleAddQuestionModalCancel}>
                Cancel
              </Button>
              <Button
                key="submit"
                type="primary"
                loading={isLoading}
                onClick={handleAddQuestionModalOk}
              >
                Add Question
              </Button>
            </>
          ) : (
            <Button
              key="continue"
              type="primary"
              loading={uploadLoading}
              disabled={uploadLoading}
              onClick={handleContinueUpload}
            >
              Continue
            </Button>
          )
        }
      >
        <Form form={form} layout="vertical" name="add_question_form">
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Add Question" key="single">
              <Form.Item
                name="question"
                label="Question"
                rules={[{ required: true, message: "Please enter the question" }]}
              >
                <Input placeholder="Enter the question" size="large" />
              </Form.Item>
              <Form.Item
                name="answer"
                label="Answer"
                rules={[{ required: true, message: "Please enter the answer" }]}
              >
                <Input.TextArea
                  placeholder="Enter the answer"
                  rows={3}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="category"
                label="Category"
                rules={[
                  { required: true, message: "Please select or add a category" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select or add a category"
                  optionFilterProp="label"
                  options={modelOptions}
                  allowClear
                  size="large"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: "8px 0" }} />
                      <Space style={{ padding: "0 8px 4px" }} align="center">
                        <Input
                          placeholder="Add new category"
                          ref={inputRef}
                          value={newCategory}
                          onChange={onNameChange}
                          onKeyDown={(e) => e.stopPropagation()}
                          onPressEnter={addItem}
                        />
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={addItem}
                        >
                          {" "}Add{" "}
                        </Button>
                      </Space>
                    </>
                  )}
                />
              </Form.Item>
            </TabPane>
            <TabPane tab="AI Generate" key="upload">
              <Dragger {...draggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {selectedFileName || "Click or drag file to upload"}
                </p>
                <p className="ant-upload-hint">
                  Support for PDF, HTML, TXT, Word files
                  <br />
                  Format: Question | Answer | Category (Optional)
                  <br />
                  Max file size: 1MB
                </p>
              </Dragger>
            </TabPane>
          </Tabs>
        </Form>
        <Modal
          open={isProcessingModalVisible}
          footer={null}
          closable={false}
          centered
        >
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Typography.Text>
                Processing your file and generating questions. This may take a minute...
              </Typography.Text>
            </div>
          </div>
        </Modal>
      </Modal>

      <Modal
        title="Confirmation"
        open={confirmModalVisible}
        onOk={handleAddQuestionModalOk}
        zIndex={9999}
        onCancel={handleAddQuestionModalCancel}
        maskClosable={false}
      >
        <p>
          Are you sure you want to add this question? Video generation will
          start soon.
        </p>
      </Modal>
      <Modal
        title="Additional Information"
        open={isAdditionalInfoModalVisible}
        onCancel={() => {
          setIsAdditionalInfoModalVisible(false);
          additionalInfoForm.resetFields();
        }}
        width={800}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsAdditionalInfoModalVisible(false);
              additionalInfoForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleAdditionalInfoSave}>
            Save
          </Button>,
        ]}
      >
        <Form
          form={additionalInfoForm}
          initialValues={{
            additional_info: additionalInfoContent[selectedQuestionIndex] || "",
          }}
        >
          <Form.Item
            name="additional_info"
            help="You can use HTML tags for formatting"
          >
            <Input.TextArea
              rows={10}
              placeholder="Enter additional information with HTML formatting..."
              style={{ fontFamily: "monospace" }}
            />
          </Form.Item>
        </Form>
        {additionalInfoContent[selectedQuestionIndex] && (
          <div style={{ marginTop: 16 }}>
            <Divider>Preview</Divider>
            <div
              dangerouslySetInnerHTML={{
                __html: additionalInfoContent[selectedQuestionIndex],
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KnowledgeVerification;